import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import type { CatalogModel } from '@/types/catalog'
import { modelsApi, type ModelEnrichment } from '@/lib/api/models'
import { sessionApi } from '@/lib/api/sessions'
import { Button } from '@/components/ui/button'
import { ScoreBar } from '@/components/catalog/ScoreBar'
import { ScoreHistoryChart, type ScoreHistoryPoint } from '@/components/catalog/ScoreHistoryChart'
import { cn } from '@/lib/utils'

interface ModelDetailPanelProps {
  model: CatalogModel | null
  onBack?: () => void
  showBackButton?: boolean
  className?: string
  overlay?: boolean
  onModelUpdated?: () => void
}

export function ModelDetailPanel({
  model,
  onBack,
  showBackButton = false,
  className,
  overlay = false,
  onModelUpdated,
}: ModelDetailPanelProps) {
  const navigate = useNavigate()
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [scoreHistory, setScoreHistory] = useState<ScoreHistoryPoint[]>([])
  const [enrichment, setEnrichment] = useState<ModelEnrichment | null>(null)
  const [enrichmentBusy, setEnrichmentBusy] = useState(false)
  const [enrichmentError, setEnrichmentError] = useState<string | null>(null)

  useEffect(() => {
    if (!model) {
      setScoreHistory([])
      setEnrichment(null)
      setEnrichmentError(null)
      return
    }

    modelsApi
      .getScoreHistory(model.id, model.categoryId)
      .then(setScoreHistory)
      .catch(() => setScoreHistory([]))

    modelsApi
      .getEnrichment(model.id)
      .then(setEnrichment)
      .catch(() => setEnrichment(null))
  }, [model?.id, model?.categoryId])

  if (!model) {
    return (
      <aside
        className={cn(
          'flex h-full items-center justify-center border-l border-border bg-card p-6 text-sm text-muted-foreground',
          className,
        )}
        data-testid="catalog-model-detail"
      >
        Select a model to view details
      </aside>
    )
  }

  const runAction = async (action: () => Promise<void>) => {
    setBusy(true)
    setActionError(null)
    try {
      await action()
      onModelUpdated?.()
    } catch {
      setActionError('Action failed. Try again.')
    } finally {
      setBusy(false)
    }
  }

  const refreshEnrichment = async () => {
    if (!model) return
    setEnrichmentBusy(true)
    setEnrichmentError(null)
    try {
      const result = await modelsApi.enrichModel(model.id)
      setEnrichment(result)
    } catch {
      setEnrichmentError('Could not fetch external enrichment.')
    } finally {
      setEnrichmentBusy(false)
    }
  }

  const openChat = async () => {
    setBusy(true)
    setActionError(null)
    try {
      const session = await sessionApi.createSession({
        modelId: model.id,
        categoryId: model.categoryId,
        title: model.displayName,
      })
      navigate('/chat', { state: { openSessionId: session.id } })
    } catch {
      setActionError('Could not open chat for this model.')
    } finally {
      setBusy(false)
    }
  }

  const canManageStatus = model.status === 'active' || model.status === 'archived'
  const canPin = model.status === 'active'
  const canArchive = model.status === 'active'
  const canRestore = model.status === 'archived'

  return (
    <aside
      className={cn(
        'flex h-full min-h-0 flex-col overflow-auto bg-card',
        overlay ? 'fixed inset-0 z-30 border-0' : 'border-l border-border',
        className,
      )}
      data-testid={overlay ? 'catalog-detail-overlay' : 'catalog-model-detail'}
    >
      <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-border bg-card/95 px-3 py-2 backdrop-blur">
        {showBackButton && onBack ? (
          <Button variant="ghost" size="sm" onClick={onBack} aria-label="Back">
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
        ) : null}
        <h2 className="min-w-0 flex-1 truncate text-lg font-semibold">{model.displayName}</h2>
      </div>

      <div className="space-y-4 p-4 text-sm">
        <p className="text-muted-foreground">{model.description}</p>

        <dl className="grid grid-cols-2 gap-3">
          <DetailItem label="Provider" value={model.providerName} />
          <DetailItem label="Context" value={`${model.contextWindow.toLocaleString()} tokens`} />
          <DetailItem
            label="Pricing"
            value={
              model.pricing.isFree
                ? 'Free'
                : `$${model.pricing.inputPerMillion} in / $${model.pricing.outputPerMillion} out per M`
            }
          />
          <div className="col-span-2 space-y-2">
            <ScoreBar
              value={model.categoryScore}
              label="Category score"
              testId="catalog-category-score-bar"
            />
            <ScoreBar
              value={model.userScore}
              label="User score"
              testId="catalog-user-score-bar"
            />
          </div>
          <DetailItem label="Status" value={model.status} />
        </dl>

        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Features
          </p>
          <ul className="flex flex-wrap gap-2 text-xs">
            {model.features.toolCalling ? <FeatureBadge label="Tool calling" /> : null}
            {model.features.vision ? <FeatureBadge label="Vision" /> : null}
            {model.features.streaming ? <FeatureBadge label="Streaming" /> : null}
            {model.isPinned ? <FeatureBadge label="Pinned" /> : null}
          </ul>
        </div>

        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            External enrichment
          </p>
          <div className="space-y-2 rounded-md border border-border bg-muted/30 p-3 text-xs">
            {enrichment?.huggingFace ? (
              <div data-testid="model-enrichment-hf">
                <p className="font-medium">Hugging Face · {enrichment.huggingFace.modelId}</p>
                <p className="text-muted-foreground">
                  {enrichment.huggingFace.downloads?.toLocaleString() ?? '—'} downloads ·{' '}
                  {enrichment.huggingFace.likes?.toLocaleString() ?? '—'} likes
                </p>
                {enrichment.huggingFace.pipelineTag ? (
                  <p className="text-muted-foreground">Pipeline: {enrichment.huggingFace.pipelineTag}</p>
                ) : null}
              </div>
            ) : null}
            {enrichment?.artificialAnalysis ? (
              <div data-testid="model-enrichment-aa">
                <p className="font-medium">
                  Artificial Analysis · {enrichment.artificialAnalysis.name ?? enrichment.artificialAnalysis.slug}
                </p>
                {enrichment.artificialAnalysis.intelligenceIndex != null ? (
                  <p className="text-muted-foreground">
                    Intelligence index: {enrichment.artificialAnalysis.intelligenceIndex.toFixed(1)}
                  </p>
                ) : null}
                {enrichment.artificialAnalysis.medianOutputTokensPerSecond != null ? (
                  <p className="text-muted-foreground">
                    Speed: {enrichment.artificialAnalysis.medianOutputTokensPerSecond.toFixed(1)} tok/s
                  </p>
                ) : null}
              </div>
            ) : null}
            {!enrichment?.huggingFace && !enrichment?.artificialAnalysis ? (
              <p className="text-muted-foreground">No external metadata yet.</p>
            ) : null}
            {enrichmentError ? <p className="text-destructive">{enrichmentError}</p> : null}
            <Button
              size="sm"
              variant="outline"
              className="min-h-9"
              disabled={enrichmentBusy}
              onClick={() => void refreshEnrichment()}
              data-testid="model-enrichment-refresh"
            >
              {enrichmentBusy ? 'Fetching…' : 'Refresh enrichment'}
            </Button>
          </div>
        </div>

        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Score history
          </p>
          <ScoreHistoryChart points={scoreHistory} />
        </div>

        {(model.status === 'hidden' || model.status === 'blacklisted') && (
          <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
            {model.status === 'blacklisted'
              ? 'This model is blacklisted in this category and will be skipped by future scans.'
              : 'This model is hidden in this category.'}
          </p>
        )}

        {actionError ? (
          <p className="text-sm text-destructive" data-testid="model-detail-action-error">
            {actionError}
          </p>
        ) : null}

        <div className="flex flex-wrap gap-2 border-t border-border pt-4" data-testid="model-detail-actions">
          {model.status === 'active' ? (
            <Button
              size="sm"
              className="min-h-11"
              disabled={busy}
              onClick={() => void openChat()}
              data-testid="model-detail-open-chat"
            >
              Open chat
            </Button>
          ) : null}
          {canPin ? (
            <Button
              size="sm"
              variant={model.isPinned ? 'default' : 'outline'}
              className="min-h-11"
              disabled={busy}
              onClick={() =>
                void runAction(() => modelsApi.setModelPinned(model.id, model.categoryId, !model.isPinned))
              }
              data-testid="model-detail-pin"
            >
              {model.isPinned ? 'Unpin' : 'Pin'}
            </Button>
          ) : null}

          {canRestore ? (
            <Button
              size="sm"
              className="min-h-11"
              disabled={busy}
              onClick={() => void runAction(() => modelsApi.restoreModel(model.id, model.categoryId))}
              data-testid="model-detail-restore"
            >
              Restore
            </Button>
          ) : null}

          {canArchive ? (
            <Button
              size="sm"
              variant="outline"
              className="min-h-11"
              disabled={busy}
              onClick={() => void runAction(() => modelsApi.archiveModel(model.id, model.categoryId))}
              data-testid="model-detail-archive"
            >
              Archive
            </Button>
          ) : null}

          {canManageStatus ? (
            <>
              <Button
                size="sm"
                variant="outline"
                className="min-h-11"
                disabled={busy}
                onClick={() => void runAction(() => modelsApi.hideModel(model.id, model.categoryId))}
                data-testid="model-detail-hide"
              >
                Hide
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="min-h-11"
                disabled={busy}
                onClick={() => void runAction(() => modelsApi.blacklistModel(model.id, model.categoryId))}
                data-testid="model-detail-blacklist"
              >
                Blacklist
              </Button>
            </>
          ) : null}
        </div>
      </div>
    </aside>
  )
}

function DetailItem({ label, value, testId }: { label: string; value: string; testId?: string }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="font-medium capitalize" data-testid={testId}>
        {value}
      </dd>
    </div>
  )
}

function FeatureBadge({ label }: { label: string }) {
  return <span className="rounded-full bg-muted px-2 py-0.5">{label}</span>
}
